import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Chip, 
  OutlinedInput, 
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CalendarToday, 
  LocationOn, 
  Business, 
  AttachMoney,
  ArrowForward,
  Quiz
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchQuizStatusCounts } from '../services/analytics';
import { getAllQuizzesAdmin } from '../services/api';
import useDebounce from '../hooks/useDebounce';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const categoryOptions = [
  { value: '', label: 'All Categories' },
  { value: 'general', label: 'General' },
  { value: 'technology', label: 'Technology' },
  { value: 'business', label: 'Business' },
  { value: 'science', label: 'Science' },
  { value: 'arts', label: 'Arts' },
  { value: 'sports', label: 'Sports' },
  { value: 'other', label: 'Other' },
];

const pricingOptions = [
  { value: '', label: 'All' },
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
];

const countryOptions = [
  { value: '', label: 'All Countries' },
  { value: 'India', label: 'India' },
  { value: 'International', label: 'International' },
];

const createdDateOptions = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Created Today' },
  { value: '2days', label: 'Created in Last 2 Days' },
  { value: '7days', label: 'Created in Last 7 Days' },
];

const QuizAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusCounts, setStatusCounts] = useState<any>({});
  const [filters, setFilters] = useState({
    status: [],
    category: '',
    organizer: '',
    pricing: '',
    country: '',
    state: '',
    search: '',
    startDate: '',
    endDate: '',
    registrationStartDate: '',
    registrationEndDate: '',
    dateRange: '',
    active: '',
    showExpired: false,
    createdDateFilter: '',
  });
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    status: [],
    category: '',
    organizer: '',
    pricing: '',
    country: '',
    state: '',
    startDate: '',
    endDate: '',
    registrationStartDate: '',
    registrationEndDate: '',
    dateRange: '',
    active: '',
    showExpired: false,
    createdDateFilter: '',
  });

  // Debounced search fields
  const debouncedSearch = useDebounce(filters.search, 400);

  useEffect(() => {
    async function loadStatusCounts() {
      try {
        const counts = await fetchQuizStatusCounts();
        setStatusCounts(counts);
      } catch (error) {
        console.error('Error loading status counts:', error);
      }
    }
    loadStatusCounts();
  }, []);

  useEffect(() => {
    async function loadQuizzes() {
      setLoading(true);
      setError(null);
      try {
        const apiFilters = {
          search: debouncedSearch.trim(),
          status: appliedFilters.status.length > 0 ? appliedFilters.status.join(',') : undefined,
          category: appliedFilters.category,
          organizer: appliedFilters.organizer,
          pricing: appliedFilters.pricing,
          country: appliedFilters.country,
          state: appliedFilters.state,
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          registrationStartDate: appliedFilters.registrationStartDate,
          registrationEndDate: appliedFilters.registrationEndDate,
          dateRange: appliedFilters.dateRange,
          active: appliedFilters.active,
          showExpired: appliedFilters.showExpired,
          createdDateFilter: appliedFilters.createdDateFilter,
        };
        const data = await getAllQuizzesAdmin(apiFilters);
        setQuizzes(data.data || []);
        setLoading(false);
      } catch (error) {
        setError('Failed to load quizzes');
        setQuizzes([]);
        setLoading(false);
      }
    }
    loadQuizzes();
  }, [debouncedSearch, appliedFilters]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleCardClick = (quiz: any) => {
    navigate(`/quizzes/${quiz._id}`);
  };

  return (
    <Box maxWidth={1200} mx="auto" p={2}>
      <Typography variant="h4" fontWeight={700} color="primary" mb={3}>
        Quiz Analytics
      </Typography>
      
      {/* Summary Cards */}
      <Box display="flex" flexWrap="wrap" gap={3} mb={4}>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Total</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.total ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Pending</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.pending ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Accepted</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.accepted ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Rejected</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.rejected ?? 0}
          </Typography>
        </Paper>
      </Box>
      
      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              onChange={e => handleFilterChange('category', e.target.value)}
              label="Category"
            >
              {categoryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="Organizer"
            value={filters.organizer}
            onChange={e => handleFilterChange('organizer', e.target.value)}
            fullWidth
          />
          
          <FormControl fullWidth>
            <InputLabel>Pricing</InputLabel>
            <Select
              value={filters.pricing}
              onChange={e => handleFilterChange('pricing', e.target.value)}
              label="Pricing"
            >
              {pricingOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select
              value={filters.country}
              onChange={e => handleFilterChange('country', e.target.value)}
              label="Country"
            >
              {countryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>State</InputLabel>
            <Select
              value={filters.state}
              onChange={e => handleFilterChange('state', e.target.value)}
              label="State"
              disabled={filters.country !== 'India'}
            >
              <MenuItem value="">All States</MenuItem>
              <MenuItem value="Andhra Pradesh">Andhra Pradesh</MenuItem>
              <MenuItem value="Arunachal Pradesh">Arunachal Pradesh</MenuItem>
              <MenuItem value="Assam">Assam</MenuItem>
              <MenuItem value="Bihar">Bihar</MenuItem>
              <MenuItem value="Chhattisgarh">Chhattisgarh</MenuItem>
              <MenuItem value="Goa">Goa</MenuItem>
              <MenuItem value="Gujarat">Gujarat</MenuItem>
              <MenuItem value="Haryana">Haryana</MenuItem>
              <MenuItem value="Himachal Pradesh">Himachal Pradesh</MenuItem>
              <MenuItem value="Jharkhand">Jharkhand</MenuItem>
              <MenuItem value="Karnataka">Karnataka</MenuItem>
              <MenuItem value="Kerala">Kerala</MenuItem>
              <MenuItem value="Madhya Pradesh">Madhya Pradesh</MenuItem>
              <MenuItem value="Maharashtra">Maharashtra</MenuItem>
              <MenuItem value="Manipur">Manipur</MenuItem>
              <MenuItem value="Meghalaya">Meghalaya</MenuItem>
              <MenuItem value="Mizoram">Mizoram</MenuItem>
              <MenuItem value="Nagaland">Nagaland</MenuItem>
              <MenuItem value="Odisha">Odisha</MenuItem>
              <MenuItem value="Punjab">Punjab</MenuItem>
              <MenuItem value="Rajasthan">Rajasthan</MenuItem>
              <MenuItem value="Sikkim">Sikkim</MenuItem>
              <MenuItem value="Tamil Nadu">Tamil Nadu</MenuItem>
              <MenuItem value="Telangana">Telangana</MenuItem>
              <MenuItem value="Tripura">Tripura</MenuItem>
              <MenuItem value="Uttar Pradesh">Uttar Pradesh</MenuItem>
              <MenuItem value="Uttarakhand">Uttarakhand</MenuItem>
              <MenuItem value="West Bengal">West Bengal</MenuItem>
              <MenuItem value="Delhi">Delhi</MenuItem>
              <MenuItem value="Jammu and Kashmir">Jammu and Kashmir</MenuItem>
              <MenuItem value="Ladakh">Ladakh</MenuItem>
              <MenuItem value="Chandigarh">Chandigarh</MenuItem>
              <MenuItem value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</MenuItem>
              <MenuItem value="Lakshadweep">Lakshadweep</MenuItem>
              <MenuItem value="Puducherry">Puducherry</MenuItem>
              <MenuItem value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            label="Search by Name"
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            fullWidth
          />
          
          <TextField
            label="Start Date"
            type="date"
            value={filters.startDate}
            onChange={e => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="End Date"
            type="date"
            value={filters.endDate}
            onChange={e => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="Registration Start Date"
            type="date"
            value={filters.registrationStartDate}
            onChange={e => handleFilterChange('registrationStartDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="Registration End Date"
            type="date"
            value={filters.registrationEndDate}
            onChange={e => handleFilterChange('registrationEndDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <FormControl fullWidth>
            <InputLabel>Show by Created Date</InputLabel>
            <Select
              value={filters.createdDateFilter}
              onChange={e => handleFilterChange('createdDateFilter', e.target.value)}
              label="Show by Created Date"
            >
              {createdDateOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box mt={2} display="flex" alignItems="center" gap={2}>
          <FormControl>
            <Box display="flex" alignItems="center">
              <input
                type="checkbox"
                id="showExpired"
                checked={filters.showExpired}
                onChange={e => handleFilterChange('showExpired', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              <label htmlFor="showExpired" style={{ cursor: 'pointer' }}>
                <Typography variant="body2">Show expired quizzes (based on registration end date)</Typography>
              </label>
            </Box>
          </FormControl>
        </Box>
        
        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button 
            variant="outlined" 
            onClick={() => {
              setFilters({
                status: [],
                category: '',
                organizer: '',
                pricing: '',
                country: '',
                state: '',
                search: '',
                startDate: '',
                endDate: '',
                registrationStartDate: '',
                registrationEndDate: '',
                dateRange: '',
                active: '',
                showExpired: false,
                createdDateFilter: '',
              });
              // Don't clear appliedFilters - keep backend filters intact
            }}
          >
            Clear Filters
          </Button>
          <Button variant="contained" color="primary" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
        </Box>
      </Paper>
      
      {/* Quizzes Cards */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          All Quizzes
        </Typography>
        
        {loading ? (
          <Typography>Loading quizzes...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            {!Array.isArray(quizzes) ? (
              <Typography>Invalid data format received</Typography>
            ) : quizzes.length === 0 ? (
              <Typography>No quizzes found.</Typography>
            ) : (
              <Box 
                display="grid" 
                gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))" 
                gap={3}
              >
                {Array.isArray(quizzes) && quizzes.map((quiz, index) => (
                  <Box key={quiz._id || quiz.id || index}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        }
                      }}
                      onClick={() => handleCardClick(quiz)}
                    >
                      <Box sx={{ position: 'relative', height: 140, width: '100%' }}>
                        {quiz.image ? (
                          <CardMedia
                            component="img"
                            height="140"
                            image={quiz.image}
                            alt={quiz.title || 'Quiz'}
                            sx={{
                              objectFit: 'cover',
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'grey.200',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = '';
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              minHeight: 140,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              background: 'linear-gradient(135deg, #5b7fff 0%, #6ec6ff 100%)',
                            }}
                          >
                            <Quiz sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                            <Typography variant="subtitle1" color="white" fontWeight={700} align="center" sx={{ px: 2, textShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
                              {quiz.title || 'Quiz'}
                            </Typography>
                          </Box>
                        )}
                        {/* Featured Chip - top left, yellow */}
                        {quiz.isFeatured && (
                          <Chip
                            label="Featured"
                            color="warning"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 10,
                              left: 10,
                              zIndex: 2,
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              backgroundColor: '#ffe066',
                              color: '#7a5c00',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                          />
                        )}
                        {/* Status Chip - top right, bold, white text, solid bg */}
                        <Chip
                          label={quiz.status || 'N/A'}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            fontWeight: 800,
                            fontSize: '0.9rem',
                            px: 1.5,
                            color: '#fff',
                            backgroundColor:
                              quiz.status === 'published'
                                ? '#43a047'
                                : quiz.status === 'draft'
                                ? '#fbc02d'
                                : quiz.status === 'ongoing'
                                ? '#2196f3'
                                : quiz.status === 'completed'
                                ? '#9c27b0'
                                : quiz.status === 'cancelled'
                                ? '#e53935'
                                : '#607d8b',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                            textTransform: 'capitalize',
                            letterSpacing: 0.5,
                          }}
                        />
                      </Box>

                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        {/* Header */}
                        <Box mb={2}>
                          <Typography variant="h6" component="h3" fontWeight={600} sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.2
                          }}>
                            {quiz.title || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Organizer */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <Business sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {quiz.organizer?.firstName} {quiz.organizer?.lastName} (@{quiz.organizer?.username})
                          </Typography>
                        </Box>

                        {/* Category */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <Quiz sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {quiz.category || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Questions Count */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <Typography variant="body2" color="text.secondary">
                            {quiz.questions?.length || 0} Questions
                          </Typography>
                        </Box>

                        {/* Dates */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {quiz.dates?.startDate ? new Date(quiz.dates.startDate).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Box>

                        {/* Pricing */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <AttachMoney sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {quiz.pricing?.type === 'free' ? 'Free' : 
                             quiz.pricing?.type === 'paid' ? `₹${quiz.pricing?.amount || 0}` : 'N/A'}
                          </Typography>
                        </Box>

                        {/* Time Limit */}
                        {quiz.timeLimit && (
                          <Box display="flex" alignItems="center" mb={1.5}>
                            <Typography variant="body2" color="text.secondary">
                              Time Limit: {quiz.timeLimit} minutes
                            </Typography>
                          </Box>
                        )}
                      </CardContent>

                      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                        <Button 
                          size="small" 
                          endIcon={<ArrowForward />}
                          sx={{ ml: 'auto' }}
                        >
                          View Details
                        </Button>
                      </CardActions>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default QuizAnalyticsPage; 